/**
 * Conflict Detector Engine — Detect and auto-resolve contradictions in SceneGraph.
 *
 * Runs AFTER inference and BEFORE prompt optimization.
 * Examples of conflicts:
 *   - night + sunlight → resolve to moonlight
 *   - peaceful mood + horror genre → warning
 *   - close-up + wide shot → keep user-specified one
 */

import type { SceneGraph } from './sceneGraph';
import { isUserSpecified } from './sceneGraph';

// ─── Conflict Types ───

export interface SceneConflict {
  id: string;
  /** The two conflicting field paths */
  fieldA: string;
  fieldB: string;
  /** Values that conflict */
  valueA: string;
  valueB: string;
  /** Human-readable description */
  description: string;
  /** How severe is this conflict */
  severity: 'info' | 'warning' | 'error';
  /** Was it auto-resolved? */
  resolved: boolean;
  /** What was the resolution? */
  resolution?: string;
}

export interface ConflictDetectionResult {
  /** The (possibly corrected) SceneGraph */
  graph: SceneGraph;
  /** All conflicts found */
  conflicts: SceneConflict[];
  /** Was any conflict auto-resolved? */
  hasAutoResolved: boolean;
  /** Summary of all conflicts */
  summary: string;
}

// ─── Conflict Rules ───

interface ConflictRule {
  name: string;
  check: (g: SceneGraph) => SceneConflict | null;
  resolve: (g: SceneGraph) => void;
}

const CONFLICT_RULES: ConflictRule[] = [
  // ── Night + Sunlight ──
  {
    name: 'night_sunlight',
    check: (g) => {
      if (
        (g.environment.time === 'night' || g.environment.time === 'midnight') &&
        g.lighting.type === 'sunlight'
      ) {
        return {
          id: 'night_sunlight',
          fieldA: 'environment.time',
          fieldB: 'lighting.type',
          valueA: g.environment.time!,
          valueB: 'sunlight',
          description: 'Night scene cannot have sunlight — resolved to moonlight',
          severity: 'error',
          resolved: false,
        };
      }
      return null;
    },
    resolve: (g) => {
      // If user specified night, change lighting. If user specified sunlight, change time.
      if (isUserSpecified(g, 'environment.time')) {
        g.lighting.type = 'moonlight';
        g.lighting.colorTemp = 'cool';
      } else if (isUserSpecified(g, 'lighting.type')) {
        g.environment.time = 'morning';
      } else {
        // Neither specified by user — prefer night + moonlight
        g.lighting.type = 'moonlight';
        g.lighting.colorTemp = 'cool';
      }
    },
  },

  // ── Day + Moonlight ──
  {
    name: 'day_moonlight',
    check: (g) => {
      if (
        (g.environment.time === 'morning' || g.environment.time === 'noon') &&
        g.lighting.type === 'moonlight'
      ) {
        return {
          id: 'day_moonlight',
          fieldA: 'environment.time',
          fieldB: 'lighting.type',
          valueA: g.environment.time!,
          valueB: 'moonlight',
          description: 'Daytime scene cannot have moonlight — resolved to natural light',
          severity: 'error',
          resolved: false,
        };
      }
      return null;
    },
    resolve: (g) => {
      if (isUserSpecified(g, 'environment.time')) {
        g.lighting.type = 'natural';
      } else if (isUserSpecified(g, 'lighting.type')) {
        g.environment.time = 'night';
      } else {
        g.lighting.type = 'natural';
      }
    },
  },

  // ── Close-up + Wide shot ──
  {
    name: 'closeup_wide_conflict',
    check: (g) => {
      if (g.camera.shotType === 'close_up' && g.camera.distance === 'far') {
        return {
          id: 'closeup_wide',
          fieldA: 'camera.shotType',
          fieldB: 'camera.distance',
          valueA: 'close_up',
          valueB: 'far',
          description: 'Close-up shot conflicts with far distance',
          severity: 'warning',
          resolved: false,
        };
      }
      if (g.camera.shotType === 'wide' && g.camera.distance === 'close') {
        return {
          id: 'wide_close',
          fieldA: 'camera.shotType',
          fieldB: 'camera.distance',
          valueA: 'wide',
          valueB: 'close',
          description: 'Wide shot conflicts with close distance',
          severity: 'warning',
          resolved: false,
        };
      }
      return null;
    },
    resolve: (g) => {
      // Priority: user-specified field wins
      if (isUserSpecified(g, 'camera.shotType') && !isUserSpecified(g, 'camera.distance')) {
        g.camera.distance = g.camera.shotType === 'close_up' ? 'close' : 'far';
      } else if (isUserSpecified(g, 'camera.distance') && !isUserSpecified(g, 'camera.shotType')) {
        g.camera.shotType = g.camera.distance === 'close' ? 'close_up' : 'wide';
      }
      // If both user-specified, keep both (user knows what they want)
    },
  },

  // ── Warm lighting + Cool color palette ──
  {
    name: 'warm_cool_conflict',
    check: (g) => {
      if (
        g.lighting.colorTemp === 'warm' &&
        (g.style.colorPalette === 'teal_orange' || g.style.mood === 'cold')
      ) {
        return {
          id: 'warm_cool',
          fieldA: 'lighting.colorTemp',
          fieldB: 'style.colorPalette',
          valueA: 'warm',
          valueB: g.style.colorPalette || g.style.mood || '',
          description: 'Warm lighting conflicts with cool color palette',
          severity: 'info',
          resolved: false,
        };
      }
      return null;
    },
    resolve: () => {
      // Info-level: don't auto-resolve, just notify
    },
  },

  // ── Peaceful mood + Horror genre ──
  {
    name: 'peaceful_horror',
    check: (g) => {
      if (g.style.mood === 'peaceful' && g.style.genre === 'horror') {
        return {
          id: 'peaceful_horror',
          fieldA: 'style.mood',
          fieldB: 'style.genre',
          valueA: 'peaceful',
          valueB: 'horror',
          description: 'Peaceful mood is unusual for horror genre',
          severity: 'warning',
          resolved: false,
        };
      }
      return null;
    },
    resolve: (g) => {
      // Only auto-resolve if mood was inferred
      if (!isUserSpecified(g, 'style.mood')) {
        g.style.mood = 'eerie';
      }
    },
  },

  // ── Indoor + Sky/Nature weather ──
  {
    name: 'indoor_weather',
    check: (g) => {
      const indoorLocations = ['laboratory', 'room', 'interior', 'building'];
      if (
        g.environment.location &&
        indoorLocations.some((l) => g.environment.location!.includes(l)) &&
        g.environment.weather &&
        g.environment.weather !== 'clear'
      ) {
        return {
          id: 'indoor_weather',
          fieldA: 'environment.location',
          fieldB: 'environment.weather',
          valueA: g.environment.location!,
          valueB: g.environment.weather!,
          description: 'Indoor location cannot have outdoor weather effects',
          severity: 'warning',
          resolved: false,
        };
      }
      return null;
    },
    resolve: (g) => {
      if (!isUserSpecified(g, 'environment.weather')) {
        g.environment.weather = null;
      }
    },
  },

  // ── Shallow DOF + Landscape/Wide ──
  {
    name: 'shallow_dof_wide',
    check: (g) => {
      if (g.camera.dof === 'shallow' && g.camera.shotType === 'extreme_wide') {
        return {
          id: 'shallow_wide',
          fieldA: 'camera.dof',
          fieldB: 'camera.shotType',
          valueA: 'shallow',
          valueB: 'extreme_wide',
          description: 'Shallow depth of field is unusual for extreme wide shots',
          severity: 'info',
          resolved: false,
        };
      }
      return null;
    },
    resolve: () => {
      // Info only
    },
  },
];

/**
 * Run the conflict detector on a SceneGraph.
 * Detects contradictions and auto-resolves when safe.
 * User-specified fields are NEVER overridden.
 */
export function detectAndResolveConflicts(input: SceneGraph): ConflictDetectionResult {
  const graph: SceneGraph = JSON.parse(JSON.stringify(input));
  const conflicts: SceneConflict[] = [];
  let hasAutoResolved = false;

  for (const rule of CONFLICT_RULES) {
    const conflict = rule.check(graph);
    if (conflict) {
      // Only auto-resolve errors and warnings (not info)
      if (conflict.severity !== 'info') {
        rule.resolve(graph);
        conflict.resolved = true;
        hasAutoResolved = true;
        conflict.resolution = `Auto-resolved by ${rule.name} rule`;
      }
      conflicts.push(conflict);
    }
  }

  const summary = conflicts.length === 0
    ? 'No conflicts detected'
    : `${conflicts.length} conflict(s) found, ${conflicts.filter((c) => c.resolved).length} auto-resolved`;

  return { graph, conflicts, hasAutoResolved, summary };
}
